import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import LazyImage from "@/my_components/Common/LazyImage";

import { getCategoryMenu } from "@/services/courseService";
import { ROUTE } from "@/route/app_routes.js";

// Matched by substring (not exact key) since real category names vary in phrasing
// ("Frontend Development", "Full-stack Development", "Mobile App Development", ...) --
// order matters, first match wins, so more specific phrases are listed before broader ones.
const CATEGORY_ICON_RULES = [
  ["full-stack", "feather-layers"],
  ["fullstack", "feather-layers"],
  ["frontend", "feather-monitor"],
  ["front-end", "feather-monitor"],
  ["backend", "feather-server"],
  ["back-end", "feather-server"],
  ["mobile", "feather-smartphone"],
  ["data", "feather-bar-chart-2"],
  ["cloud", "feather-cloud"],
  ["devops", "feather-git-branch"],
  ["blockchain", "feather-link"],
  ["ui/ux", "feather-pen-tool"],
  ["design", "feather-pen-tool"],
  ["security", "feather-shield"],
  ["cyber", "feather-shield"],
  ["product", "feather-clipboard"],
  ["web", "feather-code"],
];

const iconFor = (name) => {
  const lower = name.toLowerCase();
  const match = CATEGORY_ICON_RULES.find(([keyword]) => lower.includes(keyword));
  return match ? match[1] : "feather-layers";
};

// Small close delay (matches HeaderSearchBar-style mega-menu UX) so moving the mouse from the
// trigger into the panel -- or briefly off either -- doesn't flash the menu closed-then-open.
const CLOSE_DELAY_MS = 180;

const Category = () => {
  const [menu, setMenu] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeSlug, setActiveSlug] = useState(null);
  const [open, setOpen] = useState(false);

  const wrapperRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  useEffect(() => {
    getCategoryMenu(8)
      .then((res) => {
        const data = res.data || [];
        setMenu(data);
        if (data.length) setActiveSlug(data[0].slug);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  // Click-outside close, mirroring HeaderSearchBar.js -- lets touch/keyboard users (who opened
  // via click, see below) dismiss the menu by tapping anywhere else on the page.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => clearTimeout(closeTimeoutRef.current);
  }, []);

  const activeCategory = menu.find((c) => c.slug === activeSlug) || menu[0];

  const openMenu = () => {
    clearTimeout(closeTimeoutRef.current);
    setOpen(true);
  };

  const scheduleClose = () => {
    clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => setOpen(false), CLOSE_DELAY_MS);
  };

  // Closes immediately (no delay) -- used when a link inside the menu is actually clicked, so
  // the panel doesn't linger open after Next.js client-side navigation has already fired.
  const closeMenuNow = () => {
    clearTimeout(closeTimeoutRef.current);
    setOpen(false);
  };

  const toggleMenu = () => (open ? closeMenuNow() : openMenu());

  return (
    <div
      className={`rbt-category-menu-wrapper rbt-category-update${open ? " is-open" : ""}`}
      ref={wrapperRef}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <div
        className="rbt-category-btn"
        role="button"
        tabIndex={0}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={toggleMenu}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleMenu();
          } else if (e.key === "Escape") {
            closeMenuNow();
          }
        }}
      >
        <i title="Explore" className="feather-grid d-block d-xl-none"></i>
        <span className="category-text d-none d-xl-block">Explore</span>
        <i className="feather-chevron-down rbt-category-caret d-none d-xl-block"></i>
      </div>

      <div className="rbt-explore-mega">
        <div className="rbt-explore-mega__inner">
          {loaded && menu.length === 0 ? (
            <p className="rbt-category-empty">Courses are on their way &mdash; check back soon.</p>
          ) : (
            <>
              <ul className="rbt-explore-mega__rail">
                {menu.map((category) => (
                  <li
                    key={category.slug}
                    className={category.slug === activeSlug ? "is-active" : ""}
                    onMouseEnter={() => setActiveSlug(category.slug)}
                  >
                    <Link
                      href={`${ROUTE.course}?category=${encodeURIComponent(category.name)}`}
                      onClick={closeMenuNow}
                    >
                      <span className="rbt-explore-mega__rail-icon">
                        <i className={iconFor(category.name)}></i>
                      </span>
                      <span className="rbt-explore-mega__rail-text">{category.name}</span>
                      {!!category.course_count && (
                        <span className="rbt-explore-mega__rail-count">{category.course_count}</span>
                      )}
                      <i className="feather-chevron-right rbt-explore-mega__rail-caret"></i>
                    </Link>
                  </li>
                ))}
              </ul>

              {activeCategory && (
                <div className="rbt-explore-mega__panel">
                  <div className="rbt-explore-mega__panel-head">
                    <h6>{activeCategory.name}</h6>
                    <Link
                      href={`${ROUTE.course}?category=${encodeURIComponent(activeCategory.name)}`}
                      className="rbt-explore-mega__panel-viewall"
                      onClick={closeMenuNow}
                    >
                      View all <i className="feather-arrow-right"></i>
                    </Link>
                  </div>

                  <div className="rbt-explore-mega__courses">
                    {(activeCategory.courses || []).slice(0, 6).map((course) => (
                      <Link
                        href={`${ROUTE.course_details}${course.id}`}
                        key={course.id}
                        className="rbt-explore-mega__course"
                        onClick={closeMenuNow}
                      >
                        <span className="rbt-explore-mega__course-thumb">
                          {course.course_image_small && (
                            <LazyImage src={course.course_image_small} alt={course.title} fill sizes="52px" style={{ objectFit: "cover" }} />
                          )}
                        </span>
                        <span className="rbt-explore-mega__course-title">{course.title}</span>
                      </Link>
                    ))}
                  </div>

                  <div className="rbt-explore-mega__panel-footer">
                    <Link href={ROUTE.course} className="rbt-explore-mega__panel-cta" onClick={closeMenuNow}>
                      <span>
                        <strong>Browse every course</strong>
                        <span>{menu.reduce((sum, c) => sum + (c.course_count || 0), 0)}+ free courses across all categories</span>
                      </span>
                      <i className="feather-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
export default Category;
